<script setup lang="ts">
    import { computed, inject, onUnmounted, useId } from 'vue';
    import { PanelTabsKey, nextPanelTabId } from './panelTabs';

    const props = defineProps<{
        label: string;
        // Optional stable identifier; falls back to an auto-generated id when
        // omitted. Don't default to the label — it's translated, so the tab's
        // identity would change with the UI language and break active matching.
        value?: string | number;
        // Selects this tab initially (when <PanelTabs> is uncontrolled).
        default?: boolean;
    }>();

    const ctx = inject(PanelTabsKey);
    if (!ctx) {
        throw new Error('<PanelTab> must be used inside <PanelTabs>.');
    }

    // Stable per-instance fallback so omitting `value` still yields a language-
    // independent identity (only referenced internally in uncontrolled mode).
    const fallbackValue = nextPanelTabId();
    const tabValue = computed(() => props.value ?? fallbackValue);

    const id = useId();
    ctx.register({ id, value: tabValue.value, label: props.label, isDefault: !!props.default });
    onUnmounted(() => ctx.unregister(id));

    const isActive = computed(() => ctx.isActive(tabValue.value));
</script>

<template>
    <div v-if="isActive" :id="`${id}-panel`" role="tabpanel" :aria-labelledby="`${id}-tab`">
        <slot />
    </div>
</template>
